import { useTranslation } from 'react-i18next';
import type { MemberDto, MemberStatus } from '@pokerroom/types';

/** Level and status labels shared by the member list and member detail pages. */

// Square status labels (ZIPA COMPONENTS.md §4): the word carries the meaning, colour supports it.
const STATUS_BADGES: Record<MemberStatus, string> = {
  ACTIVE: 'bg-success/10 text-success',
  INACTIVE: 'bg-base-300 text-base-content/70',
  BLACKLISTED: 'bg-error/10 text-error font-semibold',
};

type Rgb = [number, number, number];
const INK_900_RGB: Rgb = [0x10, 0x14, 0x18]; // --ink-900

const channel = (value: number) => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const luminance = ([r, g, b]: Rgb) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

const MIN_TEXT_CONTRAST = 4.5;

const parseRgb = (value: string): Rgb | null => {
  const hex = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value)?.[1];
  if (hex) {
    const full = hex.length <= 4 ? [...hex].map((c) => c + c).join('') : hex;
    if (full.length === 8 && full.slice(6) !== 'ff') return null; // translucent
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as Rgb;
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i.exec(
    value,
  );
  if (!fn) return null;
  const alpha = fn[4] === undefined ? 1 : parseFloat(fn[4]) / (fn[4].endsWith('%') ? 100 : 1);
  return alpha < 1 ? null : [Number(fn[1]), Number(fn[2]), Number(fn[3])];
};

// Other CSS colour syntaxes (names, hsl…) are resolved by the browser when one is available.
const resolveRgb = (color: string): Rgb | null => {
  const direct = parseRgb(color.trim());
  if (direct || typeof document === 'undefined') return direct;
  const probe = document.createElement('span');
  probe.style.color = color;
  if (!probe.style.color) return null;
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();
  return parseRgb(computed);
};

/**
 * Picks the ZIPA text token that reaches 4.5:1 on a membership level colour, or null when
 * neither does (mid tones) or the colour cannot be resolved; the caller then shows a swatch.
 */
export function levelBadgeTextColor(color: string | undefined): string | null {
  // Some payloads omit the colour; treat it like an unresolvable one.
  if (typeof color !== 'string') return null;
  const rgb = resolveRgb(color);
  if (!rgb) return null;
  const bg = luminance(rgb);
  const onWhite = 1.05 / (bg + 0.05);
  const onInk = (bg + 0.05) / (luminance(INK_900_RGB) + 0.05);
  if (Math.max(onWhite, onInk) < MIN_TEXT_CONTRAST) return null;
  return onInk > onWhite ? 'var(--ink-900)' : 'var(--white)';
}

/** A level label in its own colour, or a swatch beside readable text when no text token reaches 4.5:1. */
export function LevelBadge({
  level,
  className = 'text-xs',
}: {
  level: NonNullable<MemberDto['level']>;
  className?: string;
}) {
  const text = levelBadgeTextColor(level.color);
  return text ? (
    <span
      className={`inline-flex items-center px-2 py-0.5 font-medium ${className}`}
      style={{ backgroundColor: level.color, color: text }}
    >
      {level.displayName}
    </span>
  ) : (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 font-medium border border-base-300 text-base-content ${className}`}
    >
      {level.color && (
        <span
          aria-hidden="true"
          className="inline-block w-2.5 h-2.5 border border-base-300"
          style={{ backgroundColor: level.color }}
        />
      )}
      {level.displayName}
    </span>
  );
}

export function StatusBadge({ status }: { status: MemberStatus }) {
  const { t } = useTranslation('member');
  return (
    <span
      className={`inline-flex h-6 items-center px-2 text-xs font-medium ${STATUS_BADGES[status]}`}
    >
      {t(`status_${status}`)}
    </span>
  );
}
